import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { usePortalAuth } from './PortalAuthContext';
import portalClient from './portalClient';
import { ThemeToggle } from '../design-system';
import './Portal.css';

// Shell for the client-facing portal.
//
// Deliberately NOT the staff AppShell: a client sees a much smaller app, and
// borrowing the firm's cockpit chrome would imply access they don't have. Four
// destinations, a badge for anything needing their attention, and nothing else.
const NAV = [
  { to: '/portal', label: 'Home', end: true, icon: '🏠' },
  { to: '/portal/chat', label: 'Messages', icon: '💬', badge: 'chat' },
  { to: '/portal/requests', label: 'Requests', icon: '📋', badge: 'actionable' },
  { to: '/portal/billing', label: 'Billing', icon: '🧾', badge: 'billing' },
  { to: '/portal/documents', label: 'Documents', icon: '📄' },
  { to: '/portal/compliance', label: 'Filings', icon: '📅' },
];

function PortalLayout({ title, subtitle, children, refreshKey }) {
  const { user, client, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // The badge has to reflect activity from the firm, so it re-reads whenever a
  // page signals it changed something (refreshKey) as well as on mount.
  useEffect(() => {
    portalClient
      .get('/api/portal/summary')
      .then((res) => setSummary(res.data))
      .catch(() => {});
  }, [refreshKey]);

  const badgeFor = (key) => {
    if (!summary) return 0;
    // One number for "there is something here for you": the firm's open asks
    // plus any unread replies.
    if (key === 'actionable') return (summary.awaitingYou || 0) + (summary.unreadReplies || 0);
    if (key === 'chat') return summary.unreadChat || 0;
    // Only invoices still needing action. One the client has ALREADY declared
    // payment on is deliberately excluded — badging it while the firm checks
    // the bank teaches them the number is noise.
    if (key === 'billing') return summary.awaitingPayment || 0;
    return 0;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="portal-shell">
      <header className="portal-header">
        <div className="portal-brand">
          <span className="portal-brand-mark">{(client?.name || 'C').charAt(0)}</span>
          <div className="portal-brand-text">
            <span className="portal-brand-name">{client?.name || 'Client Portal'}</span>
            <span className="portal-brand-sub">Client Portal</span>
          </div>
        </div>

        <nav className="portal-nav" aria-label="Portal sections">
          {NAV.map((item) => {
            const count = badgeFor(item.badge);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `portal-nav-link${isActive ? ' is-active' : ''}`}
              >
                <span aria-hidden="true">{item.icon}</span>
                <span>{item.label}</span>
                {count > 0 && <span className="portal-nav-badge">{count}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="portal-header-right">
          <ThemeToggle />
          <div className="portal-user">
            <button
              type="button"
              className="portal-user-btn"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
            >
              {user?.name}
              <span aria-hidden="true">▾</span>
            </button>
            {menuOpen && (
              <div className="portal-user-menu" role="menu">
                <button type="button" onClick={() => { setMenuOpen(false); navigate('/portal/set-password'); }}>
                  Change password
                </button>
                <button type="button" className="portal-danger" onClick={handleLogout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="portal-main">
        {(title || subtitle) && (
          <div className="portal-page-head">
            {title && <h1 className="portal-page-title">{title}</h1>}
            {subtitle && <p className="portal-page-sub">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>

      <footer className="portal-footer">
        Questions? Reply on any request and your accountant will see it.
      </footer>
    </div>
  );
}

export default PortalLayout;
